import { IOffersDataState } from '../../types/offersData';
import { IThunkActionCreator } from '../../types/thunk';
import { resolveOffersData } from '../../api/offersData';

import { setOffersData } from './setOffersData';
import { updateBrowserUrl } from './updateBrowserUrl';
import { ISetLocationPageParams, setPage } from '../location';
import { trackingActionUpdateListing, trackingActionShowSimilarJK, trackPromoSnippetShow } from '../analytics';
import { changePreloaderVisibility } from '../preloader';
import { getSeparateUrl, memoize } from '@cian/newbuilding-utils';
import { cleanOffersCount } from './setOffersCount';
import { setProducts } from '../analytics/actions';
import { prepareProducts } from '../../mappers';
import { getPromoSnippetVisibility } from '../../utils/promoSnippet';
import { setPromoSnippetVisibility } from '../promoSnippet';
import { setQuickLinks } from '../quickLinks';

const OFFERS_UPDATE_DIAGNOSTICS_DOMAIN = 'cls-diagnostics';
const OFFERS_UPDATE_DIAGNOSTICS_MESSAGE = 'newbuilding_search_offers_update_diagnostic';
const MAX_IDS_COUNT = 5;
const SCROLL_TO_TOP_BEFORE_SET_OFFERS_DATA_REASONS = [
  'pagination',
  'geo_filter',
  'room_filter',
  'quick_price_filter',
  'construction_status_filter',
  'more_filters_submit',
  'delete_filter_tag',
  'clear_all_filter_tags',
  'location_modal_submit',
  'manifestation_type_filter',
  'quick_manifestation_type_filter',
];

let requestIndex = 0;

export interface IGetOffersDataParams extends ISetLocationPageParams {
  reason?: string;
  shouldUpdateHistory?: boolean;
  trackingAction?(queryString: string): IThunkActionCreator;
  path?: string;
  page?: number;
}

const defaultParams = {
  shouldUpdateHistory: true,
};
const getSeparateUrlMemo = memoize(getSeparateUrl);
export const isNewDomain = memoize((fullUrl: string) => {
  if (typeof window === 'undefined' || !fullUrl) {
    return false;
  }

  const { domain: newDomain } = getSeparateUrlMemo(fullUrl);
  const { domain } = getSeparateUrlMemo(window.location.href);
  const isLocalDev = domain.indexOf('localhost') !== -1;

  return !isLocalDev && domain !== newDomain;
});
export const getOffersData = (params: IGetOffersDataParams = {}): IThunkActionCreator => {
  return (dispatch, getState, context) => {
    const { location, jsonQuery, config } = getState();
    const {
      trackingAction,
      path = location.path,
      page = 1,
      reason = 'unknown',
      shouldUpdateHistory,
    } = {
      ...defaultParams,
      ...params,
    };
    const requestId = ++requestIndex;
    const startedAt = getTimestamp();
    const startScrollY = getScrollY();
    const oldOffersData = getState().offersData;

    dispatch(changePreloaderVisibility(true));

    return resolveOffersData(context.httpApi, jsonQuery, path, location.subdomain, page)
      .then((offersData: IOffersDataState) => {
        dispatch(setPage(page, offersData, jsonQuery));

        if (shouldUpdateHistory) {
          dispatch(updateBrowserUrl(offersData));
        }

        if (isNewDomain(offersData.fullUrl)) {
          return;
        }

        scrollToTopBeforeSetOffersData(reason);

        const setOffersDataStartedAt = getTimestamp();
        dispatch(setOffersData(offersData));
        const setOffersDataFinishedAt = getTimestamp();
        logOffersUpdateDiagnostics(context.logger, {
          requestEndTime: setOffersDataFinishedAt,
          requestStartTime: startedAt,
          newOffersData: offersData,
          oldOffersData,
          page,
          path,
          reason,
          requestId,
          setOffersDataDurationMs: setOffersDataFinishedAt - setOffersDataStartedAt,
          setOffersDataEndTime: setOffersDataFinishedAt,
          setOffersDataStartTime: setOffersDataStartedAt,
          startScrollY,
        });

        const newPath = new URL(offersData.fullUrl).pathname;
        const isLinksRelevant = newPath === path;
        if (!isLinksRelevant) {
          dispatch(setQuickLinks([]));
        }

        dispatch(setProducts(prepareProducts(offersData)));
        dispatch(trackingActionUpdateListing());

        if (offersData.newbuildingPromoSnippet) {
          dispatch(trackPromoSnippetShow(offersData.newbuildingPromoSnippet.newbuilding.id, jsonQuery));
        }

        if (trackingAction) {
          dispatch(trackingAction(offersData.queryString));
        }
        if (offersData.similarNewbuildings && offersData.similarNewbuildings.length) {
          dispatch(trackingActionShowSimilarJK(jsonQuery));
        }

        const isPromoSnippetVisible =
          config.isPromoSnippetEnabled &&
          offersData.newbuildingPromoSnippet !== null &&
          getPromoSnippetVisibility(page);

        dispatch(setPromoSnippetVisibility(isPromoSnippetVisible));

        dispatch(changePreloaderVisibility(false));
        dispatch(cleanOffersCount());
      })
      .catch(() => {
        dispatch(changePreloaderVisibility(false));
        logOffersUpdateDiagnostics(context.logger, {
          requestEndTime: getTimestamp(),
          requestStartTime: startedAt,
          oldOffersData,
          page,
          path,
          reason,
          requestId,
          startScrollY,
          status: 'error',
        });
      });
  };
};

interface ILogOffersUpdateDiagnosticsParams {
  requestEndTime: number;
  requestStartTime: number;
  newOffersData?: IOffersDataState;
  oldOffersData: IOffersDataState;
  page: number;
  path: string;
  reason: string;
  requestId: number;
  setOffersDataDurationMs?: number;
  setOffersDataEndTime?: number;
  setOffersDataStartTime?: number;
  startScrollY?: number;
  status?: 'success' | 'error';
}

function logOffersUpdateDiagnostics(
  logger: { warning?: (message: string, data: { domain: string; details: string }) => void } | undefined,
  {
    requestEndTime,
    requestStartTime,
    newOffersData,
    oldOffersData,
    page,
    path,
    reason,
    requestId,
    setOffersDataDurationMs,
    setOffersDataEndTime,
    setOffersDataStartTime,
    startScrollY,
    status = 'success',
  }: ILogOffersUpdateDiagnosticsParams,
) {
  logger?.warning?.(OFFERS_UPDATE_DIAGNOSTICS_MESSAGE, {
    domain: OFFERS_UPDATE_DIAGNOSTICS_DOMAIN,
    details: JSON.stringify({
      requestId,
      reason,
      status,
      path,
      page,
      durationMs: roundNumber(requestEndTime - requestStartTime),
      requestStartTime: roundNumber(requestStartTime),
      requestEndTime: roundNumber(requestEndTime),
      setOffersDataStartTime: roundOptional(setOffersDataStartTime),
      setOffersDataEndTime: roundOptional(setOffersDataEndTime),
      setOffersDataDurationMs: roundOptional(setOffersDataDurationMs),
      startScrollY: roundOptional(startScrollY),
      endScrollY: roundOptional(getScrollY()),
      oldNewbuildingsCount: oldOffersData.newbuildings?.length,
      newNewbuildingsCount: newOffersData?.newbuildings?.length,
      oldSimilarNewbuildingsCount: oldOffersData.similarNewbuildings?.length,
      newSimilarNewbuildingsCount: newOffersData?.similarNewbuildings?.length,
      oldTotal: oldOffersData.total,
      newTotal: newOffersData?.total,
      oldNewbuildingIds: getNewbuildingIds(oldOffersData.newbuildings),
      newNewbuildingIds: newOffersData ? getNewbuildingIds(newOffersData.newbuildings) : undefined,
      oldSimilarNewbuildingIds: getNewbuildingIds(oldOffersData.similarNewbuildings),
      newSimilarNewbuildingIds: newOffersData ? getNewbuildingIds(newOffersData.similarNewbuildings) : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    }),
  });
}

function getNewbuildingIds(newbuildings?: Array<{ id?: number }>): Array<number | undefined> | undefined {
  return newbuildings?.slice(0, MAX_IDS_COUNT).map(newbuilding => newbuilding.id);
}

function getTimestamp(): number {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
}

function getScrollY(): number | undefined {
  return typeof window !== 'undefined' ? window.scrollY : undefined;
}

function scrollToTopBeforeSetOffersData(reason: string) {
  if (typeof window !== 'undefined' && SCROLL_TO_TOP_BEFORE_SET_OFFERS_DATA_REASONS.includes(reason)) {
    window.scrollTo({ top: 0 });
  }
}

function roundNumber(value: number): number {
  return Number.isFinite(value) ? Math.round(value * 1000) / 1000 : 0;
}

function roundOptional(value?: number): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? roundNumber(value) : undefined;
}
